/**
 * Utility per la ricerca testuale "trova nella pagina" dentro un articolo.
 * Corrispondenza letterale: case-insensitive e accent-insensitive, lato client.
 * Nessuno stemming (a differenza della ricerca FTS del server).
 */

export type Match = { start: number; end: number };

const COMBINING = /[̀-ͯ]/g;

// Virgolette/apostrofi tipografici → ASCII dritti. Le tastiere iOS/Android li
// inseriscono al posto di " e ': vanno normalizzati sia nella query sia nel testo
// per riconoscere le "frasi esatte" e per far combaciare parole come «d'Italia»
// (apostrofo ricurvo nel testo vs dritto nella query, e viceversa).
const DQUOTES = /[“”„‟«»″❝❞]/;
const SQUOTES = /[‘’‚‛′ʼ❛❜]/;
const DQUOTES_G = /[“”„‟«»″❝❞]/g;
const SQUOTES_G = /[‘’‚‛′ʼ❛❜]/g;

export function normalizeQuotes(s: string): string {
  return s.replace(DQUOTES_G, '"').replace(SQUOTES_G, "'");
}

/**
 * Stop word italiane (lista snowball, la stessa del dizionario `italian` di
 * Postgres). Servono per allineare la "trova nella pagina" alla ricerca FTS:
 * il server scarta queste parole, quindi non vanno evidenziate qui — altrimenti
 * una query come «il vaffanculo di trump» accende ogni «il»/«di» della pagina.
 * Confrontate in forma "folded" (minuscolo, senza accenti).
 */
const ITALIAN_STOPWORDS: ReadonlySet<string> = new Set(
  (
    'ad al allo ai agli all alla alle con col coi da dal dallo dai dagli dall dalla dalle di ' +
    'del dello dei degli dell della delle in nel nello nei negli nell nella nelle su sul sullo ' +
    'sui sugli sull sulla sulle per tra contro io tu lui lei noi voi loro mio mia miei mie tuo ' +
    'tua tuoi tue suo sua suoi sue nostro nostra nostri nostre vostro vostra vostri vostre mi ti ' +
    'ci vi lo la li le gli ne il un uno una ma ed se perché anche come dov dove che chi cui non ' +
    'più quale quanto quanti quanta quante quello quelli quella quelle questo questi questa queste ' +
    'si tutto tutti a c e i l o ho hai ha abbiamo avete hanno abbia abbiate abbiano avrò avrai avrà ' +
    'avremo avrete avranno avrei avresti avrebbe avremmo avreste avrebbero avevo avevi aveva avevamo ' +
    'avevate avevano ebbi avesti ebbe avemmo aveste ebbero avessi avesse avessimo avessero avendo ' +
    'avuto avuta avuti avute sono sei è siamo siete sia siate siano sarò sarai sarà saremo sarete ' +
    'saranno sarei saresti sarebbe saremmo sareste sarebbero ero eri era eravamo eravate erano fui ' +
    'fosti fu fummo foste furono fossi fosse fossimo fossero essendo faccio fai facciamo fanno ' +
    'faccia facciate facciano farò farai farà faremo farete faranno farei faresti farebbe faremmo ' +
    'fareste farebbero facevo facevi faceva facevamo facevate facevano feci facesti fece facemmo ' +
    'faceste fecero facessi facesse facessimo facessero facendo sto stai sta stiamo stanno stia ' +
    'stiate stiano starò starai starà staremo starete staranno starei staresti starebbe staremmo ' +
    'stareste starebbero stavo stavi stava stavamo stavate stavano stetti stesti stette stemmo ' +
    'steste stettero stessi stesse stessimo stessero stando'
  )
    .split(' ')
    .map(fold),
);

/**
 * Minuscolo + rimozione accenti preservando gli indici (1:1 con la stringa
 * originale): ogni code unit in input produce esattamente un code unit in
 * output, così gli offset trovati su `fold(text)` valgono anche su `text`.
 * (Evita il disallineamento di `text.normalize('NFD')` sull'intera stringa.)
 */
export function fold(s: string): string {
  let out = '';
  for (let i = 0; i < s.length; i++) {
    let ch = s[i];
    // Normalizza le virgolette tipografiche (1 code unit → 1 code unit, così gli
    // offset restano 1:1 con la stringa originale).
    if (DQUOTES.test(ch)) ch = '"';
    else if (SQUOTES.test(ch)) ch = "'";
    const lower = ch.toLowerCase();
    const base = lower.normalize('NFD').replace(COMBINING, '');
    out += base.length > 0 ? base[0] : lower;
  }
  return out;
}

/**
 * Estrae i termini da evidenziare da una query in stile websearch:
 * - le "frasi tra virgolette" diventano un singolo termine;
 * - i termini esclusi (prefisso `-`) vengono scartati;
 * - gli operatori `OR`/`AND` vengono ignorati.
 * Dedup case/accent-insensitive.
 */
export function extractFindTerms(query: string): string[] {
  if (!query) return [];
  // Normalizza prima le virgolette ricurve del telefono, altrimenti la regex
  // delle frasi (che cerca ") non le riconosce e la frase si spezza in parole.
  query = normalizeQuotes(query);
  const terms: string[] = [];

  // 1) Frasi tra virgolette (anche escluse: -"..."). Le rimuove dalla stringa.
  const rest = query.replace(/(-?)"([^"]*)"/g, (_m, neg: string, phrase: string) => {
    const p = phrase.trim();
    if (!neg && p) terms.push(p);
    return ' ';
  });

  // 2) Parole singole rimanenti.
  for (const tok of rest.split(/\s+/)) {
    const t = tok.trim();
    if (!t || t.startsWith('-')) continue;
    if (/^(or|and)$/i.test(t)) continue; // operatori websearch
    terms.push(t);
  }

  // Dedup sulla forma "folded".
  const seen = new Set<string>();
  const out: string[] = [];
  for (const t of terms) {
    const key = fold(t);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(t);
  }

  // Scarta le stop word, come fa il dizionario `italian` del server: evita di
  // evidenziare «il»/«di»/«e»… ovunque. Se restano solo stop word (es. l'utente
  // cerca volutamente «di» nella barra) le tengo, così la ricerca fa comunque qualcosa.
  const meaningful = out.filter((t) => !ITALIAN_STOPWORDS.has(fold(t)));
  return meaningful.length > 0 ? meaningful : out;
}

/**
 * Tutte le occorrenze (di qualunque termine) in `text`, ordinate per posizione
 * e non sovrapposte. Gli offset si riferiscono al `text` originale.
 */
export function findMatches(text: string, terms: string[]): Match[] {
  if (!text || terms.length === 0) return [];
  const hay = fold(text);
  const raw: Match[] = [];

  for (const term of terms) {
    const needle = fold(term);
    if (!needle) continue;
    let from = 0;
    for (;;) {
      const idx = hay.indexOf(needle, from);
      if (idx === -1) break;
      raw.push({ start: idx, end: idx + needle.length });
      from = idx + needle.length;
    }
  }
  if (raw.length === 0) return [];

  // Ordina per inizio (e a parità preferisce il match più lungo), poi elimina
  // le sovrapposizioni tenendo il primo.
  raw.sort((a, b) => a.start - b.start || b.end - a.end);
  const out: Match[] = [];
  let lastEnd = -1;
  for (const m of raw) {
    if (m.start >= lastEnd) {
      out.push(m);
      lastEnd = m.end;
    }
  }
  return out;
}

/**
 * Combina i parametri di ricerca della home (`q` / `testo` / `titolo` /
 * `quaderni`) in un'unica stringa, usata per il carry-over dell'evidenziazione
 * nell'articolo.
 */
export function toFindQuery(params: {
  q?: string | null;
  testo?: string | null;
  titolo?: string | null;
  quaderni?: string | null;
}): string {
  return [params.q, params.testo, params.titolo, params.quaderni]
    .filter(Boolean)
    .join(' ')
    .trim();
}
